import React from 'react';
import { PrintDataFields } from '../hooks/use-print-data';
import { InfoField } from './InfoField';
import { CommentairesWebBlock, DiagnosticSanitaireWebBlock, DiagnosticVisuelWebBlock } from './WebBlocks';
import { SectionHeader } from './SectionHeader';
import { getPrintHeaderVariant } from '../utils';

interface PrintPageProps {
  data: PrintDataFields;
  inventaireId?: number;
  isMobile?: boolean;
}

export const PrintPage = ({ data, inventaireId, isMobile = false }: PrintPageProps) => {
  return (
    <>
      {/* Page 1 */}
      <div className={`page mx-auto bg-card ${!isMobile ? 'px-[200px] 2xl:px-[400px]' : 'px-4'}`}  >
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6">
          {data.organizationLogo ? (
            <img
              src={data.organizationLogo}
              alt={data.organizationName}
              className="w-[200px] max-h-[200px] h-auto shrink-0"
            />
          ) : (
            <div className="w-[200px] h-[110px] bg-muted flex items-center justify-center border border-border shrink-0">
              <span className="text-xs text-muted-foreground">Logo</span>
            </div>
          )}
          <div className="text-xs sm:text-sm">
            <p className="font-bold text-sm sm:text-base text-[#4E5155]">{data.organizationName}</p>
            <p>{data.organizationAddress}</p>
            <p>{data.organizationCity}</p>
            <p><span className="font-bold text-xs sm:text-sm text-[#4E5155]">SIRET :</span> {data.organizationSiret}</p>
            <p className="mt-2"><span className="font-bold text-xs sm:text-sm text-[#4E5155]">Recenseur :</span> {data.user?.prenom?.toUpperCase()} {data.user?.nom?.toUpperCase()}</p>
          </div>
        </div>

        {/* Main Title */}
        <SectionHeader variant={getPrintHeaderVariant(data.inventaireType)}>
          <span className="block sm:inline">FICHE DE SYNTHESE ARBRE </span>
          <span className="block sm:inline sm:before:content-['-'] sm:before:mx-2">{inventaireId}</span>
        </SectionHeader>

        {/* Tree Identification */}
        {isMobile ? (
          <div className="py-4 space-y-4 text-xs border-b border-border">
            {/* Mobile: Ordre optimisé pour lecture verticale */}
            <div>
              <InfoField label="Date" value={data.formattedDate} />
              <InfoField label="Espèce" value={data.especeLatin} />
              <InfoField label="Genre (latin)" value={data.genreLatin} />
              <InfoField label="Nom (FR)" value={data.especeLabel} />
              <InfoField label="Cultivar" value={data.cultivar} />

            </div>
            <div>
              <InfoField label="Adresse" value={data.addressLine} />
              <InfoField label="Coordonnées GPS" value={data.gps} />
            </div>
            <div>
              <InfoField label="Code" value={data.code_site} />
              <InfoField label="Identification" value={data.num_sujet} />
            </div>
            <div>



            </div>

          </div>
        ) : (
          <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs border-b border-border">
            <div>
              <InfoField label="Date" value={data.formattedDate} />
              <InfoField label="Genre (latin)" value={data.genreLatin} />
              <InfoField label="Nom (FR)" value={data.especeLabel} />
              <InfoField label="Adresse" value={data.addressLine} />
            </div>
            <div>
              <InfoField label="Code" value={data.code_site} />
              <InfoField label="Espèce" value={data.especeLatin} />
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <InfoField label="Identification" value={data.num_sujet} />
              <InfoField label="Cultivar" value={data.cultivar} />
              <InfoField label="Coordonnées GPS" value={data.gps} />
            </div>
          </div>
        )}

        {/* Caractéristiques */}
        <SectionHeader>Caractéristiques</SectionHeader>
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs border-b border-border">
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Hauteur en m" value={data.hauteur} />
            <InfoField label="Diamètre en cm" value={data.diametre} />
            <InfoField label="Âge estimé" value={data.age_estime} />
            <InfoField label="Stade de dév." value={data.caracteristiques.stade_dev} />
            <InfoField label="Remarquable" value={data.caracteristiques.critere} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Port" value={data.caracteristiques.port_arbre} />
            <InfoField label="Tronc" value={data.caracteristiques.caract_tronc} />
            <InfoField label="Pied de l'arbre" value={data.caracteristiques.caract_pied} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2 sm:col-span-2 lg:col-span-1">
            <InfoField label="Implantation" value={data.caracteristiques.implantation} />
            <InfoField label="Domaine" value={data.caracteristiques.domaine} />
            <InfoField label="Situation" value={data.caracteristiques.situation} />
            <InfoField label="Accès" value={data.caracteristiques.accessibilite} />
          </div>
        </div>

        {/* Environnement */}
        <SectionHeader>Environnement</SectionHeader>
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs border-b border-border">
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Cohabitation" value={data.environnement.proximite} />
            <InfoField label="Demande de DICT" value={data.dictRequired} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Nuisance" value={data.environnement.nuisance} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2 sm:col-span-2 lg:col-span-1">
            <InfoField label="Fréquentation" value={data.environnement.taux_freq} />
            <InfoField label="Type de Passage" value={data.environnement.type_passage} />
          </div>
        </div>

        {/* Diagnostic visuel */}
        <SectionHeader>Diagnostic visuel</SectionHeader>
        <DiagnosticVisuelWebBlock data={data} />
      </div>

      {/* Page 2 */}
      <div className={`page mx-auto bg-card ${!isMobile ? 'px-[200px] 2xl:px-[400px]' : 'px-4'}`}>
        {/* Diagnostic sanitaire */}
        <SectionHeader className="bg-gray-500">Diagnostic sanitaire</SectionHeader>

        <DiagnosticSanitaireWebBlock data={data} />

        {/* Commentaires */}
        <CommentairesWebBlock data={data} />
      </div>
    </>
  );
};